// Browser-side product analytics (PostHog) and error reporting (Sentry).
// Both stay off until their public keys exist, both load after the page so no
// route's first download grows, and the member is identified by the app_users
// id only. The names and settings: docs/environments-and-deploys.md,
// "Analytics and errors".
import type { PostHog } from "posthog-js";
import { withoutMemberData } from "./sentryEvent";
import { installAnalytics, type EventProperties } from "./track";

export type { EventProperties } from "./track";

const POSTHOG_KEY = process.env.NEXT_PUBLIC_POSTHOG_KEY;
const POSTHOG_HOST =
  process.env.NEXT_PUBLIC_POSTHOG_HOST || "https://us.i.posthog.com";
const RECORDING_ON = process.env.NEXT_PUBLIC_POSTHOG_RECORDING === "1";
const SENTRY_DSN = process.env.NEXT_PUBLIC_SENTRY_DSN;

let posthogPromise: Promise<PostHog | null> | null = null;

function posthog(): Promise<PostHog | null> {
  const key = POSTHOG_KEY;
  if (!key || typeof window === "undefined") return Promise.resolve(null);
  posthogPromise ??= import("posthog-js")
    .then(({ default: client }) => {
      client.init(key, {
        api_host: POSTHOG_HOST,
        defaults: "2025-05-24",
        person_profiles: "identified_only",
        capture_pageview: "history_change",
        capture_pageleave: true,
        // The named events are the record. Nothing clicked or typed is
        // collected on its own.
        autocapture: false,
        mask_all_text: true,
        mask_all_element_attributes: true,
        disable_session_recording: !RECORDING_ON,
        session_recording: { maskAllInputs: true, maskTextSelector: "*" },
        respect_dnt: true,
        disable_surveys: true,
        capture_exceptions: false,
      });
      return client;
    })
    .catch(() => null);
  return posthogPromise;
}

let sentryPromise: Promise<void> | null = null;

function sentry(): Promise<void> {
  const dsn = SENTRY_DSN;
  if (!dsn || typeof window === "undefined") return Promise.resolve();
  sentryPromise ??= import("@sentry/nextjs")
    .then((Sentry) => {
      Sentry.init({
        dsn,
        environment: process.env.NEXT_PUBLIC_DEPLOY_ENV || undefined,
        tracesSampleRate: 0,
        beforeSend: withoutMemberData,
      });
    })
    .catch(() => undefined);
  return sentryPromise;
}

/** Called once per page load from src/pages/_app.tsx. Pages and components
 * never import this module; they call src/lib/analytics/track.ts, whose
 * handlers are installed here. */
export function startAnalytics(): void {
  void posthog();
  void sentry();
  installAnalytics({ track, identifyMember });
}

let identifiedMemberId: string | null = null;

/** Ties this browser's events to the signed-in member by app_users id. */
export function identifyMember(memberId: string): void {
  if (!memberId || identifiedMemberId === memberId) return;
  identifiedMemberId = memberId;
  void posthog()
    .then((client) => {
      if (client && client.get_distinct_id() !== memberId) client.identify(memberId);
    })
    .catch(() => undefined);
  void sentry()
    .then(() => (SENTRY_DSN ? import("@sentry/nextjs") : null))
    .then((Sentry) => Sentry?.setUser({ id: memberId }))
    .catch(() => undefined);
}

/** Records one product event. Never throws, never delays the caller. */
export function track(event: string, properties?: EventProperties): void {
  void posthog()
    .then((client) => client?.capture(event, properties))
    .catch(() => undefined);
}
